'use client';

import { FC, useEffect } from 'react';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';
import { SellerStaticsDetail, UserInfo } from '../layouts/defaultLayoutContainer';
import { tagManagerFragment$key } from './__generated__/tagManagerFragment.graphql';
import { useAppState } from './appState';
import config from './config';

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
        __lo_site_id: number;
    }
}

const TagManager: FC<{
    userInfo?: UserInfo;
    eCommerceName: string;
    sellerStaticsDetail?: SellerStaticsDetail;
    token?: boolean;
}> = ({ userInfo, eCommerceName, sellerStaticsDetail, token }) => {
    const { queryData } = useAppState();

    const query = token
        ? useFragment<tagManagerFragment$key>(
              graphql`
                  fragment tagManagerFragment on Query {
                      treeMenuReferringInfo {
                          isTrial
                          noValidSubscriptionReason
                          subscriptionCancel
                      }
                      ecommerceSettings {
                          currencyCode
                      }
                      countryISOCode
                      sellerIP
                  }
              `,
              queryData?.tagManagerFragment ?? null
          )
        : null;

    // Safe access to query data
    const additionalInfo = query?.treeMenuReferringInfo;
    const currencyCode = query?.ecommerceSettings?.currencyCode || 'USD';
    const countryIsoCode = query?.countryISOCode || '';
    const sellerIp = query?.sellerIP || '';

    const gtmId = 'GTM-PHGFSWR';

    let gtmUserid = -1;
    let gtmSessiondata = '';
    let gtmClientid = '';
    let gclAwCookie = '';
    let fbpCookie = '';
    let fbcCookie = '';

    useEffect(() => {
        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];

        function gtag(...args: any[]) {
            window.dataLayer.push(args);
        }

        window.gtag = gtag;

        // Initial GTM configuration
        gtag('js', new Date());
        gtag('config', 'AW-850830315', { send_page_view: false });

        // Helper function for push in dataLayer
        function addDataLayer(obj: any) {
            window.dataLayer.push(obj);
        }

        // Hash function
        async function hash(string: string) {
            const utf8 = new TextEncoder().encode(string);
            const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }

        // Get viewbag data
        const userId = userInfo?.userID || -1;
        console.log('UserID=X' + userId + 'X');

        if (userInfo && eCommerceName && sellerStaticsDetail && userId !== null && userId !== -1 && userId !== 34783) {
            const userData = {
                ecommerce: eCommerceName || 'none',
                ecommerceCurrency: currencyCode,
                isInTrial: additionalInfo?.isTrial || false,
                subscriptionCancel: additionalInfo?.subscriptionCancel || '',
                noValidSubscriptionReason: additionalInfo?.noValidSubscriptionReason || '',
                email: userInfo?.email || '',
                username: sellerStaticsDetail?.username || '',
                registeredOn: sellerStaticsDetail?.registeredOn || '',
                status: sellerStaticsDetail?.state || '',
                subStatus: sellerStaticsDetail?.subState || '',
                subscribedOn: sellerStaticsDetail?.subscribedOn || '',
                configuredOn: sellerStaticsDetail?.configuredOn || '',
                products: sellerStaticsDetail?.products || '0',
                subscriptionId: sellerStaticsDetail?.subscriptionID || 0,
                planName: sellerStaticsDetail?.planName || '',
                lastLogin: sellerStaticsDetail?.lastLogin || '',
                ecommerceUrl: sellerStaticsDetail?.eCommerceURL || '',
                orders: sellerStaticsDetail?.orders || '0',
                quantity: sellerStaticsDetail?.quantity || '0',
                lastDayOrders: sellerStaticsDetail?.lastDayOrders || '0',
                lastOrderDate: sellerStaticsDetail?.lastOrderDate || '',
                ordersAsPaying: sellerStaticsDetail?.ordersAsPaying || '0',
                totalFees: sellerStaticsDetail?.totalFees || '0',
                isExtraViews: sellerStaticsDetail?.isExtraViews || 'false',
                integrationState: sellerStaticsDetail?.integrationState || '0',
                identityProviders: sellerStaticsDetail?.identityProviderId || '',
                countryIsoCode: countryIsoCode || '',
                views: sellerStaticsDetail?.isExtraViews || '',
                sellerIp: sellerIp || ''
            };

            Promise.all([hash(userData.email), hash(userData.countryIsoCode)])
                .then(([user_email_hash, user_country_hash]) => {
                    console.log('user_email_hash>: ' + user_email_hash);
                    console.log('user_country_hash>: ' + user_country_hash);
                    addDataLayer({
                        event: 'init',
                        logged_in: true,
                        userData: {
                            user_id: userId,
                            user_email: userData.email,
                            ecommerce: userData.ecommerce,
                            currency: userData.ecommerceCurrency,
                            isInTrial: userData.isInTrial,
                            subscriptionCancel: userData.subscriptionCancel,
                            noValidSubscriptionReason: userData.noValidSubscriptionReason,
                            user_email_hash: user_email_hash,
                            username: userData.username,
                            registeredOn: userData.registeredOn,
                            status: userData.status,
                            subStatus: userData.subStatus,
                            subscribedOn: userData.subscribedOn,
                            configuredOn: userData.configuredOn,
                            products: userData.products,
                            subscriptionId: userData.subscriptionId,
                            planName: userData.planName,
                            lastLogin: userData.lastLogin,
                            ecommerceUrl: userData.ecommerceUrl,
                            orders: userData.orders,
                            quantity: userData.quantity,
                            lastDayOrders: userData.lastDayOrders,
                            lastOrderDate: userData.lastOrderDate,
                            ordersAsPaying: userData.ordersAsPaying,
                            totalFees: userData.totalFees,
                            isExtraViews: userData.isExtraViews,
                            integrationState: userData.integrationState,
                            identityProviders: userData.identityProviders,
                            user_country: userData.countryIsoCode,
                            views: userData.views,
                            user_country_hash: user_country_hash,
                            client_ip_address: userData.sellerIp
                        }
                    });

                    // Load Google Tag Manager script
                    const script = document.createElement('script');
                    script.async = true;
                    script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
                    document.head.appendChild(script);
                })
                .catch((error) => {
                    console.log('catch Promise: ' + error);
                });
        } else if (userId !== 34783) {
            console.log('>>>> Versione Ridotta Init');

            window.dataLayer.push({
                event: 'init',
                logged_in: false
            });

            // Load Google Tag Manager script
            const script = document.createElement('script');
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            document.head.appendChild(script);
        }

        window.__lo_site_id = 141930;
        if (userId && userId !== -1 && userId !== 34783) {
            let curr_gtmSessiondata = getRandomNumberAsString();
            let curr_gtmClientid = '1543082262.1684508422';
            let curr_gclAwCookie: string | null = null;
            let curr_fbpCookie: string | null = null;
            let curr_fbcCookie: string | null = null;

            try {
                const valoreLocalStorage = localStorage.getItem('gtmZakekeClientData');
                if (valoreLocalStorage) {
                    const gtmclientdataLocalStorage = JSON.parse(valoreLocalStorage);
                    if (gtmclientdataLocalStorage) {
                        gtmUserid = gtmclientdataLocalStorage.gtmuserid;
                        gtmSessiondata = gtmclientdataLocalStorage.gtmsessiondata;
                        gtmClientid = gtmclientdataLocalStorage.gtmclientid;
                        gclAwCookie = gtmclientdataLocalStorage.gclAwCookie;
                        fbpCookie = gtmclientdataLocalStorage.fbpCookie;
                        fbcCookie = gtmclientdataLocalStorage.fbcCookie;
                    } else {
                        console.log('No LocalStorage Data Parse');
                    }
                } else {
                    console.log('No LocalStorage Data');
                }

                curr_gtmClientid = getmyclientid();

                const gclAwValue = getGclAwCookieValue();
                if (gclAwValue) {
                    curr_gclAwCookie = getPartAfterSecondDot(gclAwValue) ?? '';
                }

                const fbpValue = getFBPCookieValue();
                if (fbpValue) {
                    curr_fbpCookie = fbpValue;
                }

                const fbcValue = getFBCCookieValue();
                if (fbcValue) {
                    curr_fbcCookie = fbcValue;
                }

                getSessionDataAsync()
                    .then((res) => {
                        curr_gtmSessiondata = res.ga_session_id;
                        continuaElaborazione(curr_gtmSessiondata, curr_gtmClientid, curr_gclAwCookie, curr_fbpCookie, curr_fbcCookie);
                    })
                    .catch(() => {
                        continuaElaborazione(curr_gtmSessiondata, curr_gtmClientid, curr_gclAwCookie, curr_fbpCookie, curr_fbcCookie);
                    });
            } catch (ex) {
                console.log('Error tagManager: ' + ex);
            }
        }

        function continuaElaborazione(
            p_sessionData: string,
            p_clientId: string,
            p_gclAwCookie: string | null,
            p_fbpCookie: string | null,
            p_fbcCookie: string | null
        ) {
            if (gtmUserid != userId) console.log('gtmUserid differente', gtmUserid, '->', userId);
            if (p_sessionData != gtmSessiondata) console.log('gtmSessiondata differente', gtmSessiondata, '->', p_sessionData);
            if (p_clientId != gtmClientid) console.log('gtmClientid differente', gtmClientid, '->', p_clientId);
            if (p_gclAwCookie != gclAwCookie) console.log('gclAwCookie differente', gclAwCookie, '->', p_gclAwCookie);
            if (p_fbpCookie != fbpCookie) console.log('fbpCookie differente', fbpCookie, '->', p_fbpCookie);
            if (p_fbcCookie != fbcCookie) console.log('fbcCookie differente', fbcCookie, '->', p_fbcCookie);

            if (p_sessionData && p_clientId) {
                if (
                    gtmUserid != userId ||
                    p_sessionData != gtmSessiondata ||
                    p_clientId != gtmClientid ||
                    p_gclAwCookie != gclAwCookie ||
                    p_fbpCookie != fbpCookie ||
                    p_fbcCookie != fbcCookie
                ) {
                    setGTMClientData(userId, p_sessionData, p_clientId, p_gclAwCookie, p_fbpCookie, p_fbcCookie);
                } else {
                    console.log('LocalStorage === CurrValue');
                }
            } else {
                console.log('Error: curr_gtmSessiondata OR curr_gtmClientid empty');
            }
        }

        function getSessionDataAsync(): Promise<{ ga_session_id: string; ga_session_number: string }> {
            return new Promise((resolve, reject) => {
                let numTry = 0;

                const checkCookie = () => {
                    const pattern = /_ga_QYY6QNCC7N=GS\d\.\d\.(.+?)(?:;|$)/;
                    const match = document.cookie.match(pattern);
                    const parts = match?.[1].split('.');

                    if (!parts) {
                        numTry++;
                        console.log('Try getSessionDataAsync: ' + numTry);
                        if (numTry < 4) {
                            setTimeout(checkCookie, 1000);
                        } else {
                            reject(new Error('Cookie not available after multiple tries.'));
                        }
                    } else {
                        resolve({
                            ga_session_id: parts.shift() || '',
                            ga_session_number: parts.shift() || ''
                        });
                    }
                };
                checkCookie();
            });
        }

        function getmyclientid() {
            const cookieObjCid = new URLSearchParams(document.cookie.replaceAll('&', '%26').replaceAll('; ', '&'));
            const gaClientId = cookieObjCid.get('_ga')?.match(/[0-9]+\S[0-9]+$/g);
            return gaClientId ? gaClientId[0] : '';
        }

        function setGTMClientData(
            p_userId: number,
            p_gtmSessiondata: string,
            p_gtmClientid: string | null,
            p_gclAwCookie: string | null,
            p_fbpCookie: string | null,
            p_fbcCookie: string | null
        ) {
            try {
                let url = `${config.apiUrl}GTMClientData/integration/setgtmclientdata?`;
                const params = new URLSearchParams();

                if (p_gtmSessiondata) params.append('sessionData', p_gtmSessiondata);
                if (p_gtmClientid) params.append('clientIdData', p_gtmClientid);
                if (p_gclAwCookie) params.append('gclAwCookie', p_gclAwCookie);
                if (p_fbpCookie) params.append('fbpCookie', p_fbpCookie);
                if (p_fbcCookie) params.append('fbcCookie', p_fbcCookie);

                if (params.toString()) url += params.toString();

                fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8',
                        Authorization: 'Bearer ' + config.token
                    }
                })
                    .then((response) => response.json())
                    .then((data) => {
                        if (data) {
                            console.log('Post GTMClientData: OK');
                            const gtmZakekeClientData = {
                                gtmuserid: p_userId,
                                gtmsessiondata: p_gtmSessiondata === 'undefined' ? null : p_gtmSessiondata,
                                gtmclientid: p_gtmClientid === 'undefined' ? null : p_gtmClientid,
                                gclAwCookie: p_gclAwCookie === 'undefined' ? null : p_gclAwCookie,
                                fbpCookie: p_fbpCookie === 'undefined' ? null : p_fbpCookie,
                                fbcCookie: p_fbcCookie === 'undefined' ? null : p_fbcCookie
                            };
                            localStorage.setItem('gtmZakekeClientData', JSON.stringify(gtmZakekeClientData));
                        } else {
                            console.log('Post GTMClientData: response = false');
                        }
                    })
                    .catch((error) => {
                        console.error('Post GTMClientData: Error', error);
                    });
            } catch (e) {
                console.error('Post GTMClientData: Catch', e);
            }
        }

        function getRandomNumberAsString() {
            const randomNumber = Math.floor(Math.random() * 9000000000) + 1000000000;
            return randomNumber.toString();
        }

        function getCookieValue(cookieName: string) {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.startsWith(cookieName + '=')) {
                    return cookie.substring(cookieName.length + 1);
                }
            }
            return null;
        }

        function getGclAwCookieValue() {
            return getCookieValue('_gcl_aw');
        }

        function getFBPCookieValue() {
            return getCookieValue('_fbp');
        }

        function getFBCCookieValue() {
            return getCookieValue('_fbc');
        }

        function getPartAfterSecondDot(str: string) {
            const parts = str.split('.');
            if (parts.length >= 3) {
                return parts.slice(2).join('.');
            }
            return null;
        }
    }, [gtmId]);

    return null;
};

export function addDataLayer(obj: any) {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push(obj);
    }
}

export default TagManager;
